<template>
    <div class="projects-detail-view">
        <!-- Header -->
        <HeaderComponent />

        <!-- Projects Navigation Bar -->
        <nav class="projects-navigation">
            <div class="container">
                <div class="d-flex justify-content-start align-items-center">
                    <button @click="goBack" class="btn-back">
                        &lt; zurück
                    </button>
                </div>
            </div>
        </nav>



        <!-- Projects Section -->
        <section class="projects-section">
            <div class="container">
                <div class="projects-content">
                    <!-- Left Column: Header + Filters -->
                    <div class="projects-left-column">
                        <div class="projects-header">
                            <h2 class="section-title">{{ filteredProjects.length }} Projekte</h2>
                            <p class="section-subtitle">
                                {{ dynamicSubtitle }}
                            </p>
                        </div>

                        <!-- Project Filters -->
                        <div class="projects-filters">
                            <div class="filter-buttons">
                                <button v-for="filter in filters" :key="filter.id" class="filter-btn"
                                    :class="[{ active: activeFilters.includes(filter.id) }, filter.type]"
                                    @click="toggleFilter(filter.id)">

                                    <!-- Show icon for location buttons -->
                                    <img v-if="filter.icon" :src="filter.icon" :alt="filter.label + ' Wappen'" class="town-icon">

                                    {{ filter.label }}
                                </button>
                            </div>
                            
                            <!-- Clear All Filters Button -->
                            <button v-if="activeFilters.length > 0" @click="clearAllFilters" class="clear-filters-btn">
                                Alle Filter löschen
                            </button>
                        </div>
                    </div>

                    <!-- Right Column: Map -->
                    <div class="projects-map">
                        <InteractiveMap 
                            :active-filters="activeFilters"
                            :is-static="false"
                            @project-clicked="openProjectModal"
                        />
                    </div>
                </div>
            </div>
        </section>

        <!-- Goals and Projects Section -->
        <section class="goals-projects-section">
            <div class="container">
                <div class="goals-content">
                    <!-- Left Column: Title -->
                    <div class="goals-left-column">
                        <h2 class="section-title">Meine Ziele und meine Projekte</h2>
                    </div>
                    
                    <!-- Right Column: Accordion -->
                    <div class="goals-right-column">
                        <div class="goals-accordion">
                            <div v-for="goal in allGoals" :key="goal.id" class="goal-item">
                                <div class="goal-header" @click="toggleGoal(goal.id)">
                                    <h3 class="goal-title">{{ goal.title }}</h3>
                                    <div class="goal-count">{{ getProjectsForGoal(goal.id).length }}</div>
                                </div>
                                
                                <div v-if="expandedGoals.includes(goal.id)" class="goal-content">
                                    <div class="goal-link">
                                        <a @click="goToGoal(goal.id)" class="goal-learn-more">
                                            Mehr zu diesem Ziel erfahren
                                        </a>
                                    </div>
                                    
                                    <div class="projects-list">
                                        <h4>Projekte:</h4>
                                        <ul class="project-links">
                                            <li v-for="project in getProjectsForGoal(goal.id)" :key="project.name" class="project-item">
                                                <a @click="openProjectModal(project)" class="project-link">
                                                    {{ project.name }}
                                                </a>
                                            </li>
                                        </ul>
                                    </div>
                                    
                                    <button @click="closeGoal(goal.id)" class="close-goal-btn">
                                        Ziel schließen
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Project Modal -->
        <ProjectModal v-if="showProjectModal" :project="selectedProject" @close="closeProjectModal" />

        <!-- Footer -->
        <FooterComponent />
    </div>
</template>

<script>
import HeaderComponent from '@/components/HeaderComponent.vue'
import FooterComponent from '@/components/FooterComponent.vue'
import InteractiveMap from '@/components/InteractiveMap.vue'
import ProjectModal from '@/components/ProjectModal.vue'
import projectService from '@/services/projectService.js'

export default {
    name: 'ProjectsDetailView',
    components: {
        HeaderComponent,
        FooterComponent,
        InteractiveMap,
        ProjectModal
    },
    data() {
        return {
            activeFilters: [],
            expandedGoals: [],
            showProjectModal: false,
            selectedProject: null,
            allProjects: [],
            filters: [
                // Status buttons (no icons)
                { id: 'laufend', label: 'Laufend', type: 'status' },
                { id: 'abgeschlossen', label: 'Abgeschlossen', type: 'status' },
                { id: 'geplant', label: 'Geplant', type: 'status' },

                // Location buttons (with town icons)
                {
                    id: 'niederissigheim',
                    label: 'Niederissigheim',
                    type: 'location',
                    icon: '/images/Wappen_Niederissigheim.svg'
                },
                {
                    id: 'oberissigheim',
                    label: 'Oberissigheim',
                    type: 'location',
                    icon: '/images/Wappen_Oberissigheim.svg'
                },
                {
                    id: 'rossdorf',
                    label: 'Roßdorf',
                    type: 'location',
                    icon: '/images/Wappen_Roßdorf.svg'
                },
                {
                    id: 'butterstadt',
                    label: 'Butterstadt',
                    type: 'location',
                    icon: '/images/Wappen_Butterstadt.svg'
                },
                {
                    id: 'innenstadt',
                    label: 'Innenstadt',
                    type: 'location',
                    icon: '/images/Wappen_Innenstadt.svg'
                }
            ],
            allGoals: [
                {
                    id: 1,
                    title: 'Politik mit Nähe und Persönlichkeit',
                    projectIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
                },
                {
                    id: 2,
                    title: 'Mehr Sicherheit, mehr Miteinander',
                    projectIds: [13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33]
                },
                {
                    id: 3,
                    title: 'Stadtentwicklung mit Zukunft',
                    projectIds: [34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64]
                },
                {
                    id: 4,
                    title: 'Wirtschaft und Handel - unser Motor',
                    projectIds: [64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79, 80, 81, 82, 83, 84]
                },
                {
                    id: 5,
                    title: 'Mobilität, die Bruchköbel bewegt',
                    projectIds: [85, 86, 87, 88, 89, 90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105, 106]
                },
                {
                    id: 6,
                    title: 'Kinder, Jugend & Familie – unsere Zukunft',
                    projectIds: [107, 108, 109, 110, 111, 112, 113, 114, 115, 116, 117, 118, 119]
                },
                {
                    id: 7,
                    title: 'Selbstbestimmt und gut umsorgt im Alter',
                    projectIds: [120, 121, 122, 123, 124, 125, 126, 127, 128]
                },
                {
                    id: 8,
                    title: 'Kultur leben und erleben',
                    projectIds: [129, 130, 131, 132, 133]
                },
                {
                    id: 9,
                    title: 'Bürgernahe Verwaltung – digital und persönlich',
                    projectIds: [134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 144, 145, 146, 147, 148, 149, 150, 151]
                }
            ]
        }
    },
    async mounted() {
        await this.loadProjects()
        // Check for goal anchor in URL and expand corresponding goal
        this.$nextTick(() => {
            this.handleGoalAnchor()
        })
    },
    computed: {
        filteredProjects() {
            if (this.activeFilters.length === 0) {
                return this.allProjects
            }
            
            return this.allProjects.filter(project => {
                const statusFilters = this.activeFilters.filter(f => 
                    ['laufend', 'abgeschlossen', 'geplant'].includes(f)
                )
                const locationFilters = this.activeFilters.filter(f => 
                    ['niederissigheim', 'oberissigheim', 'rossdorf', 'butterstadt', 'innenstadt'].includes(f)
                )
                
                let statusMatch = statusFilters.length === 0 || statusFilters.includes(project.status)
                let locationMatch = locationFilters.length === 0 || locationFilters.includes(project.district?.toLowerCase())
                
                return statusMatch && locationMatch
            })
        },
        dynamicSubtitle() {
            const locationFilters = this.activeFilters.filter(f => 
                ['niederissigheim', 'oberissigheim', 'rossdorf', 'butterstadt', 'innenstadt'].includes(f)
            )
            
            // If no location filters active, show all locations
            if (locationFilters.length === 0) {
                return 'Für Bruchköbel, Niederissigheim, Oberissigheim, Roßdorf und Butterstadt'
            }
            
            // Map filter IDs to display names
            const locationNames = {
                'niederissigheim': 'Niederissigheim',
                'oberissigheim': 'Oberissigheim', 
                'rossdorf': 'Roßdorf',
                'butterstadt': 'Butterstadt',
                'innenstadt': 'Innenstadt'
            }
            
            // Get the display names for active location filters
            const activeLocationNames = locationFilters.map(id => locationNames[id])
            
            // Format the text
            if (activeLocationNames.length === 1) {
                return `Für ${activeLocationNames[0]}`
            } else if (activeLocationNames.length === 2) {
                return `Für ${activeLocationNames[0]} und ${activeLocationNames[1]}`
            } else {
                // For 3+ locations, use commas and "und" for the last one
                const lastLocation = activeLocationNames.pop()
                return `Für ${activeLocationNames.join(', ')} und ${lastLocation}`
            }
        }
    },
    methods: {
        async loadProjects() {
            try {
                this.allProjects = await projectService.fetchProjects()
            } catch (error) {
                console.error('Failed to load projects:', error)
                this.allProjects = []
            }
        },
        goBack() {
            this.$router.push('/')
        },
        goToGoals() {
            this.$router.push('/goals')
        },
        goToGoal(goalId) {
            this.$router.push(`/goals#goal-${goalId}`)
        },
        toggleFilter(filterId) {
            const index = this.activeFilters.indexOf(filterId)
            if (index > -1) {
                // Filter is active, remove it
                this.activeFilters.splice(index, 1)
            } else {
                // Filter is not active, add it
                this.activeFilters.push(filterId)
            }
        },
        clearAllFilters() {
            this.activeFilters = []
        },
        toggleGoal(goalId) {
            const index = this.expandedGoals.indexOf(goalId)
            if (index > -1) {
                // If clicking on an already open accordion, close it
                this.expandedGoals.splice(index, 1)
            } else {
                // If opening a new accordion, close all others first
                this.expandedGoals = [goalId]
            }
        },
        closeGoal(goalId) {
            const index = this.expandedGoals.indexOf(goalId)
            if (index > -1) {
                this.expandedGoals.splice(index, 1)
            }
        },
        getProjectsForGoal(goalId) {
            const goal = this.allGoals.find(g => g.id === goalId)
            if (!goal) return []
            
            // First, try to match projects by the 'ziel' field from the external API
            const projectsByGoalField = this.allProjects.filter(project => {
                if (!project.ziel) return false
                
                // Handle array of goals
                if (Array.isArray(project.ziel)) {
                    return project.ziel.some(ziel => 
                        ziel && ziel.toLowerCase() === goal.title.toLowerCase()
                    )
                }
                
                // Handle single goal as string
                if (typeof project.ziel === 'string') {
                    return project.ziel.toLowerCase() === goal.title.toLowerCase()
                }
                
                return false
            })
            
            if (projectsByGoalField.length > 0) {
                return projectsByGoalField
            }
            
            // Fallback to keyword matching for backwards compatibility
            const goalKeywords = {
                1: ['politik', 'nähe', 'persönlichkeit'],
                2: ['sicherheit', 'miteinander', 'feuerwehr', 'polizei'],
                3: ['stadtentwicklung', 'zukunft', 'bau', 'planung'],
                4: ['wirtschaft', 'handel', 'gewerbe', 'unternehmen', 'stadtladen', 'citymanagement'],
                5: ['mobilität', 'verkehr', 'radweg', 'bushaltestelle'],
                6: ['kinder', 'jugend', 'familie', 'kita', 'jugendzentrum'],
                7: ['alter', 'senioren', 'seniorenbeirat'],
                8: ['kultur', 'veranstaltung', 'sommer', 'platz'],
                9: ['verwaltung', 'digital', 'bürgernähe', 'bürgernahe']
            }
            
            const keywords = goalKeywords[goalId] || []
            
            return this.allProjects.filter(project => {
                const projectText = `${project.name} ${project.description}`.toLowerCase()
                return keywords.some(keyword => projectText.includes(keyword))
            }).slice(0, goal.projectIds?.length || 10) // Limit to expected count
        },
        openProjectModal(project) {
            this.selectedProject = project
            this.showProjectModal = true
            document.body.style.overflow = 'hidden'
        },
        closeProjectModal() {
            this.showProjectModal = false
            this.selectedProject = null
            document.body.style.overflow = 'auto'
        },
        handleGoalAnchor() {
            // Check if there's a goal anchor in the URL
            const hash = window.location.hash
            if (hash && hash.startsWith('#goal-')) {
                const goalId = parseInt(hash.replace('#goal-', ''))
                if (goalId && this.allGoals.find(g => g.id === goalId)) {
                    // Expand the corresponding goal
                    this.expandedGoals = [goalId]
                    
                    // Scroll to the goals section
                    this.$nextTick(() => {
                        const goalsSection = document.querySelector('.goals-projects-section')
                        if (goalsSection) {
                            goalsSection.scrollIntoView({ behavior: 'smooth' })
                        }
                    })
                }
            }
        }
    }
}
</script>
